/*    
<summary>
  This functional component "AddCustomerManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import dayjs from "dayjs";
import {
  Field,
  FieldArray,
  Form,
  Formik,
  useFormikContext
} from "formik";
import _ from "lodash";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import Select from "react-select";
import {
  DATE_FORMAT,
  integerNumJpRegex,
  integerRegex,
  safeBase64Encode,
  stringJpRegex,
} from "../../../constants/constants";
import { ActionType } from "../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../constants/enums/module-type-enum";
import userTypeEnum from "../../../constants/enums/user-type-enum";
import { useStore } from "../../../contexts/store-provider";
import { isOperationPermittedRoleBase } from "../../../helpers/action-permission-helper";
import {
  getDaysArrayForMonth,
  getMonthsArray,
  getYearsArray,
} from "../../../helpers/date-time-helper";
import { getUserTypeFromLocalStorage } from "../../../helpers/localstorage-helper";
import { toHalfWidth } from "../../../helpers/validate-helper";
import { IOption } from "../../../models/ICommon";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputAddress from "../../../shared-components/FormikFormHandlers/FormikFormInputAddress";
import FormikFormInputForRegexCheck from "../../../shared-components/FormikFormHandlers/FormikFormInputForRegexCheck";
import FormikFormInputMobileNumber from "../../../shared-components/FormikFormHandlers/FormikFormInputMobileNumber";
import FormikFormSelect from "../../../shared-components/FormikFormHandlers/FormikFormSelect";
import FormikFormTextArea from "../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import {
  AddCustomerRegistrationSchema,
  EditCustomerRegistrationSchema,
} from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import LoaderButton from "../../../shared-components/button/loader-button";
import Label from "../../../shared-components/label/label";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../shared-components/popup/delete/delete-popup-hoc";
import { formatMessage } from "../../../translations/format-message";
import RoutesConstants from "../../routes.constant";
import { CustomerTypeEnum } from "../../../constants/enums/customer-type-enum";
import FormikFormPasswordInput from "../../../shared-components/FormikFormHandlers/FormikFormPasswordInput";
import { FormObserver } from "../../../shared-components/address/form-observer";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  getCustomerLoading: boolean;
  initialValues: any;
  allCustomerList: IOption[];
  treatmentOptions: IOption[];
  timeOptions: IOption[];
  taxOptions: IOption[];
  registrationTypeOptions: IOption[];
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
  handleDeleteSubmitHandler: () => void;
  deleteActionLoading: boolean;
  motivationOptions: IOption[];
  viewOnly: boolean;
  routeFrom: string;
}

const AddEditCustomerManagementForm = (props: IProps) => {
  const {
    showDeletePopup,
    deletePopupToggleHandler,
    handleDeleteSubmitHandler,
    deleteActionLoading,
    viewOnly,
    routeFrom,
  } = props;

  const { storeManagementStore } = useStore();
  const { GetPrefectureUsingPostalCode, prefectureByZipCode } =
    storeManagementStore;

  const handleDelete = () => {
    deletePopupToggleHandler();
  };

  const handleDeleteModalClosed = () => {
    deletePopupToggleHandler();
  };

  const [postalCode, setPostalCode] = useState<string>("");
  const userType = getUserTypeFromLocalStorage();

  const formatDate = (date: string | Date) => dayjs(date).format(DATE_FORMAT);

  useEffect(() => {
    if (postalCode && postalCode?.length == 7) {
      GetPrefectureUsingPostalCode(postalCode);
    }
  }, [postalCode]);


  return (
    <>
      {showDeletePopup && (
        <DeletePopup
          modalSubmit={handleDeleteSubmitHandler}
          modalClosed={handleDeleteModalClosed}
          isLoading={deleteActionLoading}
          title={formatMessage("delete_confirmation")}
          message={formatMessage(
            "are_you_sure_you_want_to_delete_this_customer?"
          )}
        />
      )}

      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="customer_registration" />
            ) : (
              <FormattedMessage id="customer_details" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.getCustomerLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          enableReinitialize={true}
          validationSchema={
            props.id === -1
              ? AddCustomerRegistrationSchema
              : EditCustomerRegistrationSchema
          }
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values) => {
            const formattedMotives = values.MotivationOptions.map(
              (optionValue: any) => {
                const selectedOption = props.motivationOptions.find(
                  (opt) => String(opt.id) === optionValue
                );
                if (!selectedOption) return null;
                return {
                  MId: Number(selectedOption.id),
                  CustId: values.Id ? Number(values.Id) : 0,
                };
              }
            ).filter((item: any) => item !== null);

            let isUpdated = !_.isEqual(
              props.initialValues.CustomerMotiveDetails,
              formattedMotives
            );

            const customerData = {
              FirstName: values.FirstName || null,
              LastName: values.LastName || null,
              FirstNameKana: values.FirstNameKana || null,
              LastNameKana: values.LastNameKana || null,
              Dob:
                values.DobDate &&
                values.DobMonth &&
                values.DobYear &&
                values.DobDate > 0 &&
                values.DobMonth > 0 &&
                values.DobYear > 0
                  ? `${values.DobYear}-${values.DobMonth}-${values.DobDate}`
                  : null,
              Gender: values.Gender ?? null,
              Phone1: toHalfWidth(values.Phone1) || null,
              // Phone2: values.Phone2 || null,
              Email: values.Email ? values.Email.toLowerCase() : null,
              PostalCode: toHalfWidth(values.PostalCode),
              Address: values.Address || null,
              PrefectureName: values.PrefectureName || null,
              DianaCode: values.DianaCode || null,
              AppMemberShipCode: values.AppMemberShipCode || null,
              Referer: userType === userTypeEnum[userTypeEnum.Store] ? (values.Referer > 0 ? values.Referer : null) : null,
              IsBlocked: userType === userTypeEnum[userTypeEnum.Store] ? (values.IsBlocked ?? null) : false,
              Memo: values.Memo || null,
              DmDshs: values.DmDshs === "true",
              Password: values.Password ? safeBase64Encode(values.Password) : null,
              CustomerType: props.id < 0 ? (props.initialValues.CustomerType === 1 ? 1 : (values.Password ? 1 : (userType === userTypeEnum[userTypeEnum.Store] ? 2 : 3)))
                : props.initialValues?.CustomerType === 1 ? 1 : (values.Password ? 1 : values?.CustomerType),
              RegisteredBy: 0,
              StoreId: values.StoreId ?? null,
              UserId: values.UserId ?? null,
              Status: values.Status,
            };

            let updateData = {
              ...customerData,
              IsUpdated: isUpdated,
              // Referer: (values.Referer > 0 ? values.Referer : null),
              UpdateCustomerMotiveDetails: formattedMotives.map(
                (motive: any) => ({
                  Id: motive.Id,
                  MId: motive.MId,
                  CustId: motive.CustId,
                })
              ),
            };

            props.submitHandler(
              props.id > 0
                ? updateData
                : { ...customerData, CustomerMotiveDetails: formattedMotives }
            );
          }}
        >
          {({ setFieldValue, values, errors }) => {
            return (
              <Form className="user-form">
                {props.getCustomerLoading ? (
                  <FormLoader loading={props.getCustomerLoading} />
                ) : null}
                <FormObserver
                  prefectureByZipCode={prefectureByZipCode}
                  initialPostalCode={props.initialValues.PostalCode}
                />
                <Modal.Body>
                  {/* Row 1: Last Name, First Name */}
                  <div className="row">
                    <div className="col-12 col-md-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="last_name"
                      />
                      <Field
                        name="LastName"
                        type="text"
                        className="form-control"
                        as={FormikFormInput}
                        placeholder="enter_last_name"
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-12 col-md-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="first_name"
                      />
                      <Field
                        name="FirstName"
                        type="text"
                        className="form-control"
                        as={FormikFormInput}
                        placeholder="enter_first_name"
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-12 col-md-6 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="last_name_kana"
                      />
                      <Field
                        name="LastNameKana"
                        type="text"
                        className="form-control"
                        // regex={stringJpRegex}
                        as={FormikFormInput}
                        placeholder="enter_last_name_kana"
                        disabled={viewOnly}
                      />
                    </div>
                    
                    <div className="col-12 col-md-6 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="first_name_kana"
                      />
                      <Field
                        name="FirstNameKana"
                        type="text"
                        className="form-control"
                        // regex={stringJpRegex}
                        as={FormikFormInput}
                        placeholder="enter_first_name_kana"
                        disabled={viewOnly}
                      />
                    </div>

                    <div className="col-12 col-md-6 form-group form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="date_of_birth"
                      />
                      <div className="d-flex gap-2">
                        <div className="form-group">
                          <Field
                            className="form-control"
                            name="DobYear"
                            options={getYearsArray()}
                            as={FormikFormSelect}
                            showErrorField={false}
                            disabled={viewOnly}
                          />
                        </div>
                        <div className="form-group">
                          <Field
                            className="form-control"
                            name="DobMonth"
                            options={getMonthsArray()}
                            as={FormikFormSelect}
                            showErrorField={false}
                            disabled={viewOnly}
                          />
                        </div>
                        <div className="form-group">
                          <Field
                            className="form-control"
                            name="DobDate"
                            options={getDaysArrayForMonth(
                              values.DobYear,
                              values.DobMonth
                            )}
                            as={FormikFormSelect}
                            showErrorField={false}
                            disabled={viewOnly}
                          />
                        </div>
                      </div>
                      {errors.DobYear && (
                        <div className="text-danger errorMessage">
                          {typeof errors.DobYear === "string" ? (
                            formatMessage(errors.DobYear)
                          ) : (
                            <></>
                          )}
                        </div>
                      )}
                      {/* <ErrorMessage
                        name="DobYear"
                        component="div"
                        className="text-danger small mb-2"
                      />
                      <ErrorMessage
                        name="DobMonth"
                        component="div"
                        className="text-danger small mb-2"
                      />
                      <ErrorMessage
                        name="DobDate"
                        component="div"
                        className="text-danger small mb-2"
                      /> */}
                    </div>
                    <div className="col-12 col-md-6 form-group form-group">
                      <Label
                        className="label-style"
                        label="gender"
                        required={false}
                      />
                      <div className="row g-1">
                        <div className="col-4">
                          <Field
                            className="form-check-input"
                            name="Gender"
                            type="radio"
                            value="0"
                            id="male"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="male">
                            <FormattedMessage id="male" />
                          </label>
                        </div>
                        <div className="col-4">
                          <Field
                            className="form-check-input"
                            name="Gender"
                            type="radio"
                            value="1"
                            id="female"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="female">
                            <FormattedMessage id="female" />
                          </label>
                        </div>
                        <div className="col-4">
                          <Field
                            className="form-check-input"
                            name="Gender"
                            type="radio"
                            value="2"
                            id="not_set"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="not_set">
                            <FormattedMessage id="not_set" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 form-group">
                      <Label
                        className="label-style"
                        required
                        label="contact_number"
                      />
                      <Field
                        className="form-control"
                        name="Phone1"
                        type="text"
                        placeholder="enter_mobile_number"
                        regex={integerRegex}
                        as={FormikFormInputMobileNumber}
                        disabled={viewOnly}
                      />
                    </div>

                    {/* <div className="col-12 col-md-4 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="phone2"
                      />
                      <Field
                        name="Phone2"
                        type="text"
                        className="form-control"
                        placeholder="enter_mobile_number"
                        regex={integerRegex}
                        as={FormikFormInputMobileNumber}
                      />
                    </div> */}

                    <div className="col-12 col-md-6 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="email"
                      />
                      <Field
                        className="form-control"
                        name="Email"
                        type="email"
                        autoComplete="new-email"
                        placeholder="enter_email"
                        validcharacter={["@", "#", "$", "-", "_", "."]}
                        as={FormikFormInput}
                        disabled={(props.id > 0 && (props.initialValues.CustomerType === CustomerTypeEnum.Registered) ) ? true : (viewOnly ? viewOnly : false)}
                      />
                    </div>
                    <div className="col-12 postal-code-width form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="postal_code"
                      />
                      <Field
                        className="form-control"
                        name="PostalCode"
                        type="text"
                        placeholder="enter_post_code"
                        regex={integerNumJpRegex}
                        setState={setPostalCode}
                        as={FormikFormInputForRegexCheck}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-12 next-postal-code-width form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="address"
                      />
                      <Field
                        className="form-control"
                        name="Address"
                        type="text"
                        placeholder="enter_address"
                        prefectureName={values.PrefectureName}
                        validcharacter={["@", "#", "$", "-", "_", ".", "/"]}
                        as={FormikFormInputAddress}
                        disabled={viewOnly}
                      />
                    </div>

                    {(props.initialValues.CustomerType != 1 && (userType === userTypeEnum[userTypeEnum.Store] || userType === userTypeEnum[userTypeEnum.HeadQuarter])) && (
                      <>
                        <div className="col-sm-6 mb-3">
                          <Label
                            className="label-style"
                            required={false}
                            label="new_password"
                          />
                          <div className="form-group">
                            <Field
                              className="form-control "
                              name="Password"
                              autoComplete="new-password"
                              placeholder="enter_new_password"
                              invalidcharacter={[" "]}
                              validcharacter={[
                                "~",
                                "`",
                                "!",
                                "@",
                                "#",
                                "$",
                                "%",
                                "^",
                                "&",
                                "*",
                                "(",
                                ")",
                                "_",
                                "-",
                                "+",
                                "=",
                                "{",
                                "[",
                                "}",
                                "]",
                                "|",
                                "\\",
                                ":",
                                ";",
                                "'",
                                '"',
                                "<",
                                ",",
                                ">",
                                ".",
                                "?",
                                "/",
                              ]}
                              as={FormikFormPasswordInput}
                            />
                          </div>
                        </div>
                        <div className="col-sm-6 mb-3 form-group">
                          <Label
                            className="label-style"
                            required={false}
                            label="confirm_password"
                          />
                          <div className="form-group">
                            <Field
                              className="form-control"
                              name="ConfirmPassword"
                              placeholder="enter_confirm_password"
                              as={FormikFormPasswordInput}
                              invalidcharacter={[" "]}
                              validcharacter={[
                                "~",
                                "`",
                                "!",
                                "@",
                                "#",
                                "$",
                                "%",
                                "^",
                                "&",
                                "*",
                                "(",
                                ")",
                                "_",
                                "-",
                                "+",
                                "=",
                                "{",
                                "[",
                                "}",
                                "]",
                                "|",
                                "\\",
                                ":",
                                ";",
                                "'",
                                '"',
                                "<",
                                ",",
                                ">",
                                ".",
                                "?",
                                "/",
                              ]}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {props.id > 0 && (
                      <div className={`col-12 ${userType === userTypeEnum[userTypeEnum.Store] ? "col-md-4" : "col-md-6"} form-group`}>
                        <Label className="label-style" label="diana_code" />
                        <Field
                          name="DianaCode"
                          placeholder="diana_code"
                          type="text"
                          className="form-control"
                          disabled={props.id > 0 ? true : false}
                          as={FormikFormInput}
                        />
                      </div>
                    )}
                    <div
                      className={`col-12 form-group ${
                        (props.id > 0 && userType === userTypeEnum[userTypeEnum.Store]) ? "col-md-4" : "col-md-6"
                      }`}
                    >
                      <Label
                        className="label-style"
                        label="app_membership_code"
                      />
                      <Field
                        name="AppMemberShipCode"
                        type="text"
                        placeholder="app_membership_code"
                        className="form-control"
                        disabled={
                          props.id > 0 &&
                          userType === userTypeEnum[userTypeEnum.HeadQuarter]
                            ? true
                            : (viewOnly ? viewOnly : false)
                        }
                        as={FormikFormInput}
                      />
                    </div>
                    {
                      userType === userTypeEnum[userTypeEnum.Store] &&
                      <>
                        <div
                          className={`col-12 form-group ${
                            props.id > 0 ? "col-md-4" : "col-md-6"
                          }`}
                        >
                          <Label
                            className="label-style"
                            required={false}
                            label="referer"
                          />
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              value={props.allCustomerList.find(
                                (obj) => obj.id === values.Referer
                              )}
                              isDisabled={false}
                              isLoading={false}
                              isClearable={true}
                              isRtl={false}
                              noOptionsMessage={() =>
                                formatMessage("no_customer_found")
                              }
                              onChange={(e: any) => {
                                let custId = typeof e?.id === "string" ? parseInt(e?.id) : e?.id;
                                // setCustomerName(e?.value);
                                setFieldValue("Referer", custId)
                              }}
                              isSearchable={true}
                              name="ParkingLotDropdown"
                              options={props.allCustomerList?.filter(
                                (obj) => obj?.id !== values.Referer
                              )}
                            />
                        </div>
                        <div className="col-12 form-group">
                          <Label
                            className="label-style"
                            label="motivation_to_visit"
                          />
                          <div className="motivation-box">
                            {props.motivationOptions.length > 1 ? (
                              <FieldArray name="MotivationOptions">
                                {({ push, remove, form }) => {
                                  const motivationOptions =
                                    form.values.MotivationOptions || [];
                                  return (
                                    <div className="motivation-box">
                                      {props.motivationOptions
                                        .filter(
                                          (option: IOption) => option.id !== -1
                                        )
                                        .map((option: IOption) => {
                                          const optionValue = String(option.id);
                                          const isChecked =
                                            motivationOptions.includes(optionValue);
                                          return (
                                            <label key={option.id} className="me-3">
                                              <input
                                                type="checkbox"
                                                name="MotivationOptions"
                                                className="me-1"
                                                value={optionValue}
                                                checked={isChecked}
                                                onChange={(e) => {
                                                  let newOptions = [
                                                    ...motivationOptions,
                                                  ];
                                                  if (e.target.checked) {
                                                    if (
                                                      !newOptions.includes(
                                                        optionValue
                                                      )
                                                    ) {
                                                      newOptions.push(optionValue);
                                                    }
                                                  } else {
                                                    newOptions = newOptions.filter(
                                                      (val) => val !== optionValue
                                                    );
                                                  }
                                                  form.setFieldValue(
                                                    "MotivationOptions",
                                                    newOptions
                                                  );
                                                }}
                                                disabled={viewOnly}
                                              />
                                              {option.value}
                                            </label>
                                          );
                                        })}
                                    </div>
                                  );
                                }}
                              </FieldArray>
                            ) : (
                              <p className="text-danger">
                                {formatMessage(
                                  "please_add_store_motivation_settings"
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      </>
                    }


                    {/* Memo */}
                    <div className="col-12 form-group">
                      <Label className="label-style" label="memo" />
                      <Field
                        name="Memo"
                        as={FormikFormTextArea}
                        className="form-control"
                        placeholder="enter_additional_information_here"
                        disabled={viewOnly}
                      />
                      <p className="small text-muted text-end mt-1">{`${values.Memo.length}/300`}</p>
                    </div>

                    <div className="col-12 col-md-4 form-group">
                      <Label
                        className="label-style"
                        required={false}
                        label="dm"
                      />
                      <div className="row">
                        <div className="col-12 col-md-6 form-group">
                          <Field
                            className="form-check-input"
                            name="DmDshs"
                            type="radio"
                            value="true"
                            id="yes"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="yes">
                            <FormattedMessage id="yes" />
                          </label>
                        </div>
                        <div className="col-12 col-md-6 form-group">
                          <Field
                            className="form-check-input"
                            name="DmDshs"
                            type="radio"
                            value="false"
                            id="no"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="no">
                            <FormattedMessage id="no" />
                          </label>
                        </div>
                      </div>
                    </div>
                    {
                      (userType === userTypeEnum[userTypeEnum.HeadQuarter] || props.id > 0) &&
                      <div className="col-12 col-md-4 form-group">
                        <Label
                          className="label-style"
                          required={false}
                          label="status"
                        />
                        <div className="row">
                          <div className="col-12 col-md-6 form-group">
                            <Field
                              className="form-check-input"
                              name="Status"
                              type="radio"
                              value="1"
                              id="valid"
                              disabled={viewOnly || userType === userTypeEnum[userTypeEnum.Store]}
                            />
                            <label className="form-check-label" htmlFor="valid">
                              <FormattedMessage id="valid" />
                            </label>
                          </div>
                          <div className="col-12 col-md-6 form-group">
                            <Field
                              className="form-check-input"
                              name="Status"
                              type="radio"
                              value="0"
                              id="invalid"
                              disabled={viewOnly || userType === userTypeEnum[userTypeEnum.Store]}
                            />
                            <label className="form-check-label" htmlFor="invalid">
                              <FormattedMessage id="invalid" />
                            </label>
                          </div>
                        </div>
                      </div>
                    }
                    {
                      userType === userTypeEnum[userTypeEnum.Store] &&
                      <div className="col-12 col-md-4 form-group">
                        <div className="d-flex align-items-center pt-4 mt-2">
                          <input
                            type="checkbox"
                            className="form-check-input m-0"
                            name="IsBlocked"
                            checked={values.IsBlocked}
                            onChange={(e) =>
                              setFieldValue("IsBlocked", e.target.checked)
                            }
                            disabled={viewOnly}
                          />
                          <Label
                            className="form-check-label ms-2"
                            required={false}
                            label="blacklist"
                          />
                        </div>
                      </div>
                    }
                    {
                      (userType === userTypeEnum[userTypeEnum.Store] && props.id > 0) &&
                      <>
                      <div className="col-12 col-md-4 form-group">
                        <Label
                          className="label-style"
                          required={false}
                          label="first_visit_date"
                        />
                        <Field
                          name="FirstVisitDate"
                          type="text"
                          className="form-control"
                          as={FormikFormInput}
                          placeholder="first_visit_date"
                          disabled={true}
                        />
                      </div>
                      <div className="col-12 col-md-4 form-group">
                        <Label
                          className="label-style"
                          required={false}
                          label="last_visit_date"
                        />
                        <Field
                          name="LastVisitDate"
                          type="text"
                          className="form-control"
                          as={FormikFormInput}
                          placeholder="last_visit_date"
                          disabled={true}
                        />
                      </div>
                      <div className="col-12 col-md-4 form-group">
                        <Label
                          className="label-style"
                          required={false}
                          label="repeat_visit_count"
                        />
                        <Field
                          name="TotalCount"
                          type="text"
                          className="form-control"
                          as={FormikFormInput}
                          placeholder="repeat_visit_count"
                          disabled={true}
                        />
                      </div>
                     </>
                    }

                  </div>
                </Modal.Body>

                {/* Footer Buttons */}
                <div className="modal-footer center-content">
                  <Button
                    variant="outline-danger"
                    type="button"
                    onClick={props.onModalClosed}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    <FormattedMessage id="close" />
                  </Button>
                  {isOperationPermittedRoleBase(
                    ModuleType.CustomerManagement,
                    ActionType.Edit
                  ) && (
                    <LoaderButton
                      type="submit"
                      iconName="bi-check-lg"
                      isLoading={props.getCustomerLoading}
                      text={props.id > -1 ? "button_update" : "button_register"}
                    />
                  )}
                  {routeFrom !== RoutesConstants.SalesMedicalRecords &&
                    props.id > -1 &&
                    isOperationPermittedRoleBase(
                      ModuleType.CustomerManagement,
                      ActionType.Delete
                    ) && (
                      <Button
                        variant="danger"
                        type="button"
                        onClick={handleDelete}
                        disabled={props.getCustomerLoading}
                      >
                        <i className="bi bi-trash3-fill me-1"></i>
                        <FormattedMessage id="button_delete" />
                      </Button>
                    )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default DeletePopupHOC(observer(AddEditCustomerManagementForm));
